import type { KyInstance } from 'ky';

import { ensureError, MINUTE } from '@enterprise/core';
import type { FileUpload } from '@enterprise/types';

import { Uri } from '#constants/uri';

/**
 * Uploads one or more template files to the server.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param typeName - The type name associated with the template upload.
 * @param fileUploads - An array of file uploads, each containing a file and its associated metadata.
 * @returns A promise that resolves with the server's response as JSON.
 * @throws Throws an error if the upload fails.
 */
export const uploadTemplate = async (instance: KyInstance, typeName: string, fileUploads: FileUpload[]) => {
  try {
    const formData = new FormData();

    for (const { file, metaData } of fileUploads) {
      formData.append('metaData', JSON.stringify({ ...metaData, fileSize: file.size }));
      formData.append('file', file);
    }

    return await instance
      .extend({
        retry: 0,
        timeout: 5 * MINUTE,
      })
      .post(Uri.files.template, { body: formData, searchParams: { typeName } })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
